import { useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { sendAccountMessage } from "@/lib/brokerAccounts";
import { messageFor } from "@/lib/errors";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { buttonClass } from "@/components/buttonStyles";
import {
  MESSAGE_BODY_MAX,
  MESSAGE_SUBJECT_MAX,
  accountMessageTopicLabel,
  isAccountMessageTopic,
} from "@/lib/accountMessage";
// A broker "message us" form on the account page (BRK-S159, pairs PMS-S356). The
// message is recorded in Signal's Communication Log — durable, account-scoped, and
// answered by our team within an SLA — deliberately DISTINCT from the Crisp live
// chat widget (BRK-S136), which is for quick, ephemeral questions. Client
// validation mirrors the PMS schema (subject/body required, bounded); the server
// fixes source=broker / direction=inbound / medium=portal.
//
// With `topics` (BRK-S398, the Surety profile's dialog) a Topic select leads,
// the subject becomes optional, and the topic travels as its own typed field
// (BRK-S431, PMS-S842) — the subject as typed, or not at all.
// (The submission page's SB-number prefix composer is gone since BRK-S502: it
// messages on the order's own thread.) `framed={false}`
// drops the card and its heading for a host that supplies its own (a Dialog's
// title), and `onCancel` adds the dialog's Cancel beside Send.

export function AccountMessageForm({
  accountId,
  topics,
  framed = true,
  onCancel,
}: {
  accountId: string;
  /** PMS topic values (`ACCOUNT_MESSAGE_TOPICS`). */
  topics?: readonly string[];
  framed?: boolean;
  onCancel?: () => void;
}) {
  const { authedRequest } = useAuth();
  const [topic, setTopic] = useState(topics?.[0] ?? "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentNumber, setSentNumber] = useState<string | null>(null);

  const withTopic = topic !== "";
  const typedTopic = isAccountMessageTopic(topic) ? topic : null;
  const subjectMax = MESSAGE_SUBJECT_MAX;
  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  const valid =
    (withTopic || trimmedSubject.length > 0) &&
    trimmedSubject.length <= subjectMax &&
    trimmedBody.length > 0 &&
    trimmedBody.length <= MESSAGE_BODY_MAX;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const receipt = await sendAccountMessage(
        authedRequest,
        accountId,
        typedTopic
          ? {
              topic: typedTopic,
              ...(trimmedSubject ? { subject: trimmedSubject } : {}),
              body: trimmedBody,
            }
          : { subject: trimmedSubject, body: trimmedBody },
      );
      setSentNumber(receipt.communicationNumber);
      setSubject("");
      setBody("");
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const intro = (
    <p className="text-sm text-brk-dim">
      Send our team a message about this account — we&rsquo;ll follow up and
      keep it on the account&rsquo;s record. For a quick question, use the chat
      button instead.
    </p>
  );

  const send = (
    <button
      type="submit"
      disabled={busy || !valid}
      className={`${buttonClass()} ${onCancel ? "" : "self-start"}`}
    >
      {busy ? "Sending…" : "Send message"}
    </button>
  );

  const content = (
    <>
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {sentNumber && (
        <p role="status" className="text-sm text-green-700 dark:text-green-300">
          ✓ Message sent ({sentNumber}). We&rsquo;ll be in touch.
        </p>
      )}

      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        {topics && (
          <SelectField
            label="Topic"
            value={topic}
            options={topics.map((t) => ({
              value: t,
              label: isAccountMessageTopic(t) ? accountMessageTopicLabel(t) : t,
            }))}
            onChange={(e) => {
              setTopic(e.target.value);
              setSentNumber(null);
            }}
          />
        )}
        <TextField
          label="Subject"
          value={subject}
          maxLength={subjectMax}
          optional={withTopic}
          onChange={(e) => {
            setSubject(e.target.value);
            setSentNumber(null);
          }}
          required={!withTopic}
        />
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-brk-text">Message</span>
          <textarea
            aria-label="Message"
            value={body}
            rows={4}
            maxLength={MESSAGE_BODY_MAX}
            onChange={(e) => {
              setBody(e.target.value);
              setSentNumber(null);
            }}
            className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
          />
        </label>
        {onCancel ? (
          // Mobile first: stacked full-width at 375px with Send on top; side by
          // side from `sm:`, Send on the right, as a dialog's actions sit.
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className={buttonClass({ variant: "secondary" })}
            >
              Cancel
            </button>
            {send}
          </div>
        ) : (
          send
        )}
      </form>
    </>
  );

  if (!framed) {
    return (
      <div className="flex flex-col gap-4">
        {intro}
        {content}
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading">Message us about this account</h2>
        {intro}
      </div>
      {content}
    </section>
  );
}
